import React, { useState, useEffect } from 'react';
import SEO from '../components/common/SEO';
import LeadForm from '../components/LeadForm';
import { 
  Timer, Target, ChartBar, ShieldCheck, 
  CheckCircle, Lock, CaretDown, CaretUp 
} from '@phosphor-icons/react';
import { trackEvent } from '../lib/analytics';



const ROICalculator = () => {
  const [employees, setEmployees] = useState(5);
  const [hourlyRate, setHourlyRate] = useState(40);
  
  const totalLicenseCost = employees * 360;
  const wastedLicenseCost = totalLicenseCost * 0.85;

  const annualSavingsPerEmp = 2 * 48 * hourlyRate; // 2 hours per week
  const totalSavings = Math.round((employees * 0.50) * annualSavingsPerEmp); // 50% adoption

  const handleEmployeesChange = (e) => {
    const val = Number(e.target.value);
    setEmployees(val);
    trackEvent('roi_calculator_interacted', { employees: val, hourlyRate, totalSavings });
  };

  const handleHourlyRateChange = (e) => {
    const val = Number(e.target.value);
    setHourlyRate(val);
    trackEvent('roi_calculator_interacted', { employees, hourlyRate: val, totalSavings });
  };
  
  return (
    <div style={{ background: 'white', borderRadius: '24px', padding: '40px', boxShadow: '0 20px 40px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', maxWidth: '800px', margin: '60px auto 20px' }}>
      <h3 style={{ fontSize: '1.8rem', fontWeight: 800, marginBottom: '24px', textAlign: 'center', color: '#0f172a' }}>Justify the Spend: ROI Calculator</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '32px' }}>
        <div>
          <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px', color: '#475569' }}>Number of Copilot Licenses</label>
          <input type="range" min="5" max="200" value={employees} onChange={handleEmployeesChange} style={{ width: '100%', marginBottom: '16px' }} />
          <div style={{ fontWeight: 800, fontSize: '1.2rem', color: '#0071e3', marginBottom: '24px' }}>{employees} Licenses</div>
          
          <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px', color: '#475569' }}>Avg Hourly Loaded Rate ($)</label>
          <input type="range" min="30" max="200" value={hourlyRate} onChange={handleHourlyRateChange} style={{ width: '100%', marginBottom: '16px' }} />
          <div style={{ fontWeight: 800, fontSize: '1.2rem', color: '#0071e3' }}>${hourlyRate}/hr</div>
        </div>
        
        <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Without Training: Wasted Cost</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#e11d48' }}>${wastedLicenseCost.toLocaleString()} / yr</div>
          </div>
          <div style={{ height: '1px', background: '#cbd5e1', margin: '16px 0' }}></div>
          <div>
            <div style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>With Training: Projected Savings</div>
            <div style={{ fontSize: '2rem', fontWeight: 900, color: '#10b981' }}>${totalSavings.toLocaleString()} / yr</div>
            <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '8px', lineHeight: 1.5 }}>*Based on highly conservative 2 hours saved/week at 50% adoption.</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const deckSlides = [
  { src: '/images/acme_slides/acme_slides.001.jpg', label: 'ACME Deck - Title Slide' },
  { src: '/images/acme_slides/acme_slides.002.jpg', label: 'ACME Deck - Market Analysis' },
  { src: '/images/acme_slides/acme_slides.003.jpg', label: 'ACME Deck - Financial Projections' },
  { src: '/images/acme_slides/acme_slides.004.jpg', label: 'ACME Deck - Cost Breakdown' },
  { src: '/images/acme_slides/acme_slides.005.jpg', label: 'ACME Deck - Growth Strategy' },
  { src: '/images/acme_slides/acme_slides.006.jpg', label: 'ACME Deck - Risk Assessment' },
  { src: '/images/acme_slides/acme_slides.007.jpg', label: 'ACME Deck - Conclusion' }
];

function DeckCarousel() {
  const [page, setPage] = useState(0);
  const total = deckSlides.length;

  useEffect(() => {
    const timer = setInterval(() => {
      setPage((prev) => (prev + 1) % total);
    }, 4000);
    return () => clearInterval(timer);
  }, [total]);

  const prev = () => {
    const nextP = (page - 1 + total) % total;
    setPage(nextP);
    trackEvent('deck_carousel_navigate', { slide: nextP + 1, direction: 'prev' });
  };
  
  const next = () => {
    const nextP = (page + 1) % total;
    setPage(nextP);
    trackEvent('deck_carousel_navigate', { slide: nextP + 1, direction: 'next' });
  };

  const selectPage = (i) => {
    setPage(i);
    trackEvent('deck_carousel_navigate', { slide: i + 1, direction: 'indicator' });
  };

  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{ position: 'relative', borderRadius: '16px', overflow: 'hidden', border: '1px solid #E5E7EB', boxShadow: '0 25px 60px -12px rgba(0,0,0,0.15)', background: 'white', maxWidth: '100%', margin: '0 auto' }}>
        <img
          src={deckSlides[page].src}
          alt={deckSlides[page].label}
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />
        <button onClick={prev} aria-label="Previous slide" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '40px', height: '40px', borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.9)', boxShadow: '0 4px 14px rgba(0,0,0,0.15)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', color: '#374151', backdropFilter: 'blur(8px)', transition: 'all 0.2s' }}>
          ←
        </button>
        <button onClick={next} aria-label="Next slide" style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', width: '40px', height: '40px', borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.9)', boxShadow: '0 4px 14px rgba(0,0,0,0.15)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', color: '#374151', backdropFilter: 'blur(8px)', transition: 'all 0.2s' }}>
          →
        </button>
        <div style={{ position: 'absolute', top: '16px', right: '16px', background: 'rgba(0,0,0,0.65)', color: 'white', padding: '6px 14px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '700', backdropFilter: 'blur(8px)', letterSpacing: '0.5px' }}>
          Slide {page + 1} of {total}
        </div>
      </div>
      <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '1rem', fontWeight: '600', color: '#4B5563' }}>
        {deckSlides[page].label}
      </p>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '12px' }}>
        {deckSlides.map((_, i) => (
          <button key={i} onClick={() => selectPage(i)} aria-label={`Go to slide ${i + 1}`} style={{ width: i === page ? '28px' : '10px', height: '10px', borderRadius: '10px', border: 'none', background: i === page ? '#0f172a' : '#D1D5DB', cursor: 'pointer', transition: 'all 0.3s', padding: 0 }} />
        ))}
      </div>
    </div>
  );
}

const faqData = [
  {
    q: "Is our confidential financial data safe during onsite training?",
    a: "We sign enterprise NDAs prior to the session, and all hands-on exercises use your organisation's approved Microsoft 365 tools under your existing tenant permissions and access controls. Your financial data remains protected within your Microsoft 365 environment. Microsoft states that Copilot prompts, responses, and Microsoft Graph data are not used to train its foundation models."
  },
  {
    q: "What exactly will our team have at the end of the day?",
    a: "By the end of the workshop your team will have applied Copilot to real finance workflows and built at least one working finance use case during the session, on your own data. We agree the specific use case with you beforehand so it is realistic for your team."
  },
  {
    q: "How is this different from standard IT or Microsoft partner training?",
    a: "Standard training is delivered by IT generalists who teach generic prompt writing like email summaries. Our workshops focus on building functional Copilot Agents & SharePoint Agents that automate Trial Balances, GL reconciliations, and board reporting."
  },
  {
    q: "What if our team members have varying levels of AI experience?",
    a: "Our curriculum is structured to accommodate all skill levels. Because the 1-day session is private to your company, we calibrate the speed and exercises directly to your team's existing workflow and tooling."
  },
  {
    q: "Are there training grants or subsidies available for this workshop in Singapore?",
    a: "Corporate training and capability development may be offset through applicable Singapore schemes depending on your entity eligibility. Financial institutions can explore IBF-STS course subvention options (up to 50%–70% for qualifying tracks), while Singapore-registered employers can apply SkillsFuture Enterprise Credit (SFEC) or Enterprise Development Grant (EDG) funding toward approved capability projects."
  }
];

function FinanceFAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (idx) => {
    const nextIdx = openIndex === idx ? null : idx;
    setOpenIndex(nextIdx);
    if (nextIdx !== null) {
      trackEvent('faq_expanded', { question: faqData[idx].q });
    }
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      {faqData.map((item, idx) => (
        <div key={idx} style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', marginBottom: '16px', overflow: 'hidden', transition: 'all 0.2s' }}>
          <button
            onClick={() => toggle(idx)}
            style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', outline: 'none' }}
          >
            <span style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a', paddingRight: '16px' }}>{item.q}</span>
            {openIndex === idx ? <CaretUp size={24} color="#0071e3" weight="bold" /> : <CaretDown size={24} color="#64748b" weight="bold" />}
          </button>
          {openIndex === idx && (
            <div style={{ padding: '0 24px 24px 24px', color: '#475569', fontSize: '1.05rem', lineHeight: 1.7, borderTop: '1px solid #f1f5f9' }}>
              <p style={{ marginTop: '16px', margin: 0 }}>{item.a}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function CopilotFinanceTraining() {
  const sectionLight = { padding: '80px 0', background: '#f8fafc' };
  const sectionWhite = { padding: '80px 0', background: 'white' };
  const sectionDark = { padding: '80px 0', background: '#0f172a' };
  const container = { maxWidth: '1100px', margin: '0 auto', padding: '0 24px' };
  const card = { borderRadius: '20px', border: '1px solid rgba(148,163,184,0.16)', background: 'white', padding: '28px 32px' };

  return (
    <>
      <SEO
        title="Microsoft Copilot Training for Finance Professionals | Eagentix"
        description="1-day onsite Microsoft Copilot training for finance teams. Hands-on with your actual finance data — Excel analysis, month-end, FP&A, reporting, plus practical finance agents."
        pathname="/copilot-finance-training"
      />

      {/* Responsive Styles for this page */}
      <style>{`
        @media (max-width: 1024px) {
          .hero-h1 { font-size: 2.8rem !important; }
          .hero-p { font-size: 1.1rem !important; }
          .grid-3 { grid-template-columns: repeat(2, 1fr) !important; }
          .split-flex { flex-direction: column !important; gap: 40px !important; }
          .split-flex > div { width: 100% !important; padding: 0 !important; }
          .badge-row { flex-wrap: wrap !important; gap: 8px !important; }
          .badge-row > div { flex: 1 1 calc(50% - 8px) !important; }
        }
        @media (max-width: 768px) {
          .hero-h1 { font-size: 2.2rem !important; }
          .grid-4 { grid-template-columns: 1fr !important; }
          .badge-row { flex-direction: column !important; align-items: stretch !important; }
          .badge-row > div { width: 100% !important; }
          .hero-bg { background-position: right center !important; }
          .ba-row { grid-template-columns: 1fr !important; }
          .ba-row > div:nth-child(2) { transform: rotate(90deg); }
        }
      `}</style>

      {/* Keyframe animation for floating 3D graphics */}
      <style>{`
        @keyframes float3D {
          0% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-12px) rotate(0.5deg); }
          100% { transform: translateY(0px) rotate(0deg); }
        }
        .floating-hero-3d {
          animation: float3D 6s ease-in-out infinite;
        }
      `}</style>

      {/* Hero */}
      <section style={{ 
        paddingTop: '120px', 
        paddingBottom: '160px',
        minHeight: '750px',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        background: '#020617',
        overflow: 'hidden'
      }}>
        {/* Background Video (Visible Opacity & Correct zIndex Stack) */}
        <video 
          autoPlay 
          loop 
          playsInline 
          muted 
          style={{ 
            position: 'absolute', 
            top: '50%', 
            left: '50%', 
            width: '100%', 
            height: '100%', 
            objectFit: 'cover', 
            transform: 'translate(-50%, -50%)', 
            zIndex: 1, 
            opacity: 0.65 
          }}
        >
          <source src="/videos/finance-bg.mp4" type="video/mp4" />
        </video>

        {/* Semi-transparent Overlay for Text Legibility */}
        <div style={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'linear-gradient(to right, rgba(2, 6, 23, 0.92) 0%, rgba(15, 23, 42, 0.75) 50%, rgba(2, 6, 23, 0.88) 100%)', 
          zIndex: 2 
        }} />

        {/* Ambient Glow */}
        <div style={{ position: 'absolute', top: '20%', right: '10%', width: '600px', height: '600px', background: 'radial-gradient(circle, rgba(14, 165, 233, 0.25) 0%, rgba(139, 92, 246, 0.15) 40%, transparent 70%)', pointerEvents: 'none', zIndex: 2 }} />
        
        <div style={{ ...container, position: 'relative', zIndex: 3, maxWidth: '1280px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '48px', alignItems: 'center' }}>
            <div style={{ maxWidth: '680px', minWidth: 0 }}>
              <span style={{ color: '#bae6fd', fontWeight: 800, fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', marginBottom: '16px' }}>
                1-Day Finance Training • Delivered Onsite at Your Office
              </span>
              <h1 className="hero-h1" style={{ fontSize: '3.6rem', fontWeight: 900, color: '#ffffff', letterSpacing: '-0.03em', margin: '0 0 24px 0', lineHeight: 1.1 }}>
                Stop Bleeding ROI on <span style={{ color: '#f87171' }}>Unused</span> Copilot Licenses.
              </h1>
              <p style={{ fontSize: '1.25rem', color: '#cbd5e1', lineHeight: 1.7, marginBottom: '32px', fontWeight: 500 }}>
                Your team tried Copilot. The results weren't good enough. So they went back to the old way. We train them onsite, on your actual data, to build working Copilot workflows for month-end close — in one day.
              </p>
              
              {/* Value Badges */}
              <div className="badge-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '32px', minWidth: 0 }}>
                <div style={{ background: 'rgba(15, 23, 42, 0.75)', border: '1px solid rgba(52, 211, 153, 0.3)', padding: '12px 16px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px', backdropFilter: 'blur(8px)', minWidth: 0, flex: '1 1 auto' }}>
                  <Timer size={28} color="#34d399" weight="duotone" />
                  <div>
                    <div style={{ color: '#34d399', fontWeight: 900, fontSize: '0.95rem', lineHeight: 1 }}>Private 1 Day</div>
                    <div style={{ color: '#ffffff', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '4px' }}>Intensive</div>
                  </div>
                </div>
                
                <div style={{ background: 'rgba(15, 23, 42, 0.85)', border: '1px solid rgba(192, 132, 252, 0.4)', padding: '12px 16px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px', backdropFilter: 'blur(8px)', minWidth: 0, flex: '1 1 auto' }}>
                  <Target size={28} color="#c084fc" weight="duotone" />
                  <div>
                    <div style={{ color: '#c084fc', fontWeight: 900, fontSize: '0.95rem', lineHeight: 1 }}>Finance Built</div>
                    <div style={{ color: '#ffffff', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '4px' }}>FP&A Workflows</div>
                  </div>
                </div>

                <div style={{ background: 'rgba(15, 23, 42, 0.85)', border: '1px solid rgba(251, 191, 36, 0.4)', padding: '12px 16px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px', backdropFilter: 'blur(8px)', minWidth: 0, flex: '1 1 auto' }}>
                  <ChartBar size={28} color="#fbbf24" weight="duotone" />
                  <div>
                    <div style={{ color: '#fbbf24', fontWeight: 900, fontSize: '0.95rem', lineHeight: 1 }}>Real Data</div>
                    <div style={{ color: '#ffffff', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '4px' }}>GLs & Balances</div>
                  </div>
                </div>
              </div>

              <div>
                <a
                  href="#lead-form"
                  onClick={() => trackEvent('cta_click', { button: 'Hero Book 1-Day Training' })}
                  style={{ 
                    background: 'linear-gradient(135deg, #0071e3, #2563eb)', 
                    color: '#ffffff', 
                    padding: '20px 48px', 
                    borderRadius: '12px', 
                    fontWeight: 900, 
                    textDecoration: 'none', 
                    fontSize: '1.25rem',
                    boxShadow: '0 15px 35px -5px rgba(37, 99, 235, 0.6)',
                    display: 'inline-block',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Book Your 1-Day Training →
                </a>
                <div style={{ marginTop: '20px', color: '#ffffff', fontSize: '1.05rem', fontWeight: 700 }}>
                  ✓ We come to your office. Private session, actual finance data.
                </div>
                <div style={{ marginTop: '12px', color: '#94a3b8', fontSize: '0.95rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Lock size={18} color="#38bdf8" weight="fill" />
                  Runs inside your Microsoft 365 tenant, under your existing access controls. Enterprise NDA.
                </div>
              </div>
            </div>

            {/* 3D Fintech Animated Visual (Dribbble Inspired: Bullo & Payment Platform 3D) */}
            <div className="floating-hero-3d" style={{ position: 'relative', minWidth: 0, width: '100%', borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.2)', boxShadow: '0 30px 70px -15px rgba(14, 165, 233, 0.4)', background: 'rgba(15, 23, 42, 0.8)' }}>
              <img
                src="/images/onsite-training.png"
                alt="Private onsite Copilot training session with a finance team"
                style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '24px' }}
              />

              {/* Dribbble-style Floating Glass Pill 1 */}
              <div style={{
                position: 'absolute',
                top: '20px',
                left: '20px',
                background: 'rgba(15, 23, 42, 0.85)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(52, 211, 153, 0.4)',
                padding: '10px 18px',
                borderRadius: '16px',
                color: 'white',
                boxShadow: '0 10px 25px rgba(0,0,0,0.4)',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                zIndex: 5
              }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#34d399', boxShadow: '0 0 10px #34d399' }} />
                <div>
                  <div style={{ fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 800 }}>Automated Close</div>
                  <div style={{ fontSize: '0.95rem', fontWeight: 900, color: '#34d399' }}>15 Min Reconciliation</div>
                </div>
              </div>

              {/* Dribbble-style Floating Glass Pill 2 */}
              <div style={{
                position: 'absolute',
                bottom: '24px',
                right: '24px',
                background: 'rgba(15, 23, 42, 0.85)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(14, 165, 233, 0.4)',
                padding: '12px 20px',
                borderRadius: '18px',
                color: 'white',
                boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                zIndex: 5
              }}>
                <div style={{ fontSize: '0.75rem', color: '#38bdf8', textTransform: 'uppercase', fontWeight: 800 }}>Microsoft Copilot Agent</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 900, color: 'white', marginTop: '2px' }}>Real Data • GL Sync</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 70% Hands-On + capability strip */}
      <section style={{ background: '#0f172a', padding: '48px 0', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ ...container, textAlign: 'center' }}>
          <h2 style={{ fontSize: '2.2rem', fontWeight: 900, color: 'white', letterSpacing: '-0.02em', margin: '0 0 12px' }}>
            30% Learn. <span style={{ color: '#38bdf8' }}>70% Do.</span>
          </h2>
          <p style={{ color: '#cbd5e1', fontSize: '1.1rem', lineHeight: 1.6, maxWidth: '640px', margin: '0 auto 28px' }}>
            No generic AI theory. Your team spends the day applying Copilot to real finance workflows.
          </p>
          <div style={{ color: '#94a3b8', fontSize: '0.8rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '16px' }}>
            In One Day, Your Team Learns to Use Copilot For
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px' }}>
            {['Excel Analysis', 'Variance Analysis', 'Month-End Close', 'FP&A', 'Management Reports', 'PowerPoint Decks'].map((c) => (
              <span key={c} style={{ background: 'rgba(56, 189, 248, 0.1)', border: '1px solid rgba(56, 189, 248, 0.35)', color: '#e0f2fe', padding: '10px 20px', borderRadius: '999px', fontSize: '0.95rem', fontWeight: 700 }}>
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Above the fold testimonials */}
      <section style={{ background: '#090d16', padding: '40px 0 64px 0', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={container}>
          <div className="grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', marginBottom: '0' }}>
            <div style={{ background: 'rgba(15, 23, 42, 0.85)', padding: '24px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)', boxShadow: '0 10px 30px -10px rgba(0,0,0,0.5)' }}>
              <div style={{ color: '#fbbf24', fontSize: '1.2rem', marginBottom: '8px' }}>★★★★★</div>
              <p style={{ color: '#ffffff', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: '16px', fontWeight: 500 }}>"Every month-end was a nightmare with manual consolidations. Eagentix Trainer showed us how to use Copilot to automate our variance analysis. Our close is 3 days faster now."</p>
              <div style={{ color: '#ffffff', fontWeight: 800, fontSize: '1rem' }}>Head of Finance</div>
              <div style={{ color: '#e2e8f0', fontSize: '0.9rem', marginTop: '4px', fontWeight: 600 }}>Global Manufacturing Company · 1,000+ employees</div>
            </div>
            
            <div style={{ background: 'rgba(15, 23, 42, 0.85)', padding: '24px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)', boxShadow: '0 10px 30px -10px rgba(0,0,0,0.5)' }}>
              <div style={{ color: '#fbbf24', fontSize: '1.2rem', marginBottom: '8px' }}>★★★★★</div>
              <p style={{ color: '#ffffff', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: '16px', fontWeight: 500 }}>"We were paying for the licenses but getting terrible outputs. The training on Copilot optimisations and prompt effectiveness changed everything. We now generate accurate ad-hoc reports in minutes."</p>
              <div style={{ color: '#ffffff', fontWeight: 800, fontSize: '1rem' }}>VP Finance</div>
              <div style={{ color: '#e2e8f0', fontSize: '0.9rem', marginTop: '4px', fontWeight: 600 }}>Enterprise Technology Company</div>
            </div>

            <div style={{ background: 'rgba(15, 23, 42, 0.85)', padding: '24px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)', boxShadow: '0 10px 30px -10px rgba(0,0,0,0.5)' }}>
              <div style={{ color: '#fbbf24', fontSize: '1.2rem', marginBottom: '8px' }}>★★★★★</div>
              <p style={{ color: '#ffffff', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: '16px', fontWeight: 500 }}>"I used to spend three nights copy-pasting numbers from Excel to PowerPoint for board reporting. Using the storytelling module, Copilot formats our raw data into a polished deck in 15 minutes."</p>
              <div style={{ color: '#ffffff', fontWeight: 800, fontSize: '1rem' }}>CFO</div>
              <div style={{ color: '#e2e8f0', fontSize: '0.9rem', marginTop: '4px', fontWeight: 600 }}>Series B SaaS Company</div>
            </div>
          </div>
          <p style={{ textAlign: 'center', color: '#64748b', fontSize: '0.85rem', marginTop: '20px', marginBottom: 0, fontStyle: 'italic' }}>
            Client names withheld under enterprise NDA — the same confidentiality that covers your training session.
          </p>
        </div>
      </section>

      {/* Private Delivery */}
      <section style={{ ...sectionLight, overflow: 'hidden' }}>
        <div style={container}>
          <div className="split-flex" style={{ display: 'flex', alignItems: 'center', gap: '64px', flexWrap: 'wrap', flexDirection: 'row-reverse' }}>
            <div style={{ flex: '1 1 500px', position: 'relative' }}>
              <img src="/images/hero-banner.png" alt="Private onsite Copilot finance training in Singapore" style={{ width: '100%', borderRadius: '24px', boxShadow: '0 20px 40px -12px rgba(0,0,0,0.15)', aspectRatio: '16/9', objectFit: 'cover', objectPosition: 'right center' }} />
            </div>
            <div style={{ flex: '1 1 400px' }}>
              <span style={{ color: '#0ea5e9', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', marginBottom: '16px' }}>Private Onsite Delivery</span>
              <h2 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '24px', lineHeight: 1.1 }}>
                We Bring the AI to Your Finance Team.
              </h2>
              <p style={{ color: '#475569', fontSize: '1.1rem', lineHeight: 1.7, marginBottom: '24px' }}>
                Forget crowded hotel conference rooms. Our Copilot experts come directly to your office to coach your team in a private, confidential setting. 
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: '#0f172a', fontSize: '1rem', lineHeight: 1.6, display: 'flex', flexDirection: 'column', gap: '12px', fontWeight: 500 }}>
                  <li style={{ paddingLeft: '28px', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#0ea5e9', fontWeight: 800 }}>✓</span>
                    <strong>Go beyond prompting:</strong> learn to create practical Copilot & SharePoint Agents for finance workflows
                  </li>
                  <li style={{ paddingLeft: '28px', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#0ea5e9', fontWeight: 800 }}>✓</span>
                    Hands-on with your actual GL data and finance workflows
                  </li>
                  <li style={{ paddingLeft: '28px', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#0ea5e9', fontWeight: 800 }}>✓</span>
                    Secure environment for sensitive financial information
                  </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What Changes the Next Morning */}
      <section style={sectionWhite}>
        <div style={container}>
          <div style={{ textAlign: 'center', marginBottom: '48px', maxWidth: '800px', margin: '0 auto 48px' }}>
            <span style={{ color: '#0071e3', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              Behavioural Change, Not a Curriculum
            </span>
            <h2 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', margin: '16px 0 16px' }}>
              What Changes the Next Morning
            </h2>
            <p style={{ color: '#475569', fontSize: '1.1rem', lineHeight: 1.7 }}>
              After one day, your team stops working the old way. Here's what that looks like in their actual jobs.
            </p>
          </div>
          <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[
              { before: 'Manually trawl GL exports to explain variances', after: 'Use Copilot to identify and explain budget-vs-actual variances' },
              { before: 'Write management commentary from scratch', after: 'Draft commentary directly from the financial analysis' },
              { before: 'Spend hours formatting board decks', after: 'Turn analysis into management-ready PowerPoints in minutes' },
              { before: 'Search policies and prior reports by hand', after: 'Ask Copilot questions across approved finance documents' },
              { before: 'Repeat the same Excel analysis every close', after: 'Use Copilot to accelerate formulas, reconciliations and summaries' },
            ].map((row, i) => (
              <div key={i} className="ba-row" style={{ display: 'grid', gridTemplateColumns: '1fr 40px 1fr', alignItems: 'center', gap: '16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px 24px' }}>
                <div style={{ color: '#64748b', fontSize: '1rem', lineHeight: 1.5 }}>{row.before}</div>
                <div style={{ color: '#0071e3', fontWeight: 900, fontSize: '1.3rem', textAlign: 'center' }}>→</div>
                <div style={{ color: '#0f172a', fontSize: '1rem', fontWeight: 700, lineHeight: 1.5 }}>{row.after}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Pain Point */}
      <section style={{ ...sectionLight }}>
        <div style={container}>
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '24px' }}>
              The Hidden Cost of Generic Training
            </h2>
            <p style={{ fontSize: '1.35rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.5, marginBottom: '16px' }}>
              This isn't a Copilot demo. It's a working session for finance professionals.
            </p>
            <p style={{ color: '#475569', fontSize: '1.15rem', lineHeight: 1.8, marginBottom: '32px' }}>
              Your finance team doesn't need to learn how to write polite emails. They need to automate month-end close. When you send FP&A professionals to generic AI training, they revert to manual VLOOKUPs the very next day.
            </p>

            <div style={{ margin: '32px auto 40px', maxWidth: '950px', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 20px 40px rgba(15, 23, 42, 0.12)', border: '1px solid #e2e8f0' }}>
              <img src="/images/generic-vs-specialized.jpg" alt="Generic AI Training vs Specialized Copilot Finance AI Training" style={{ width: '100%', height: 'auto', display: 'block' }} />
            </div>

            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginTop: '40px', alignItems: 'stretch' }}>
              {/* Generic Training */}
              <div style={{ flex: 1, minWidth: '300px', background: 'white', borderRadius: '24px', padding: '32px', border: '1px solid #f1f5f9', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', textAlign: 'left' }}>
                <div style={{ color: '#ef4444', fontWeight: 700, fontSize: '1.2rem', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</div>
                  Generic External Training
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: '#475569', fontSize: '1rem', lineHeight: 1.7, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <li style={{ paddingLeft: '24px', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#ef4444' }}>•</span>
                    <strong>Waste of time:</strong> Teaches your team how to "summarize meetings" and draft emails.
                  </li>
                  <li style={{ paddingLeft: '24px', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#ef4444' }}>•</span>
                    <strong>Blind spots:</strong> Taught by IT generalists who don't even know what a Trial Balance is.
                  </li>
                  <li style={{ paddingLeft: '24px', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#ef4444' }}>•</span>
                    <strong>Irrelevant data:</strong> Uses dummy HR or Marketing datasets that finance can't relate to.
                  </li>
                  <li style={{ paddingLeft: '24px', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#ef4444' }}>•</span>
                    <strong>Low ROI:</strong> Focuses on basic chat instead of automating complex financial models.
                  </li>
                </ul>
              </div>

              {/* Our Specialized Training */}
              <div style={{ flex: 1, minWidth: '300px', background: 'linear-gradient(180deg, #f8fafc 0%, white 100%)', borderRadius: '24px', padding: '32px', border: '2px solid #0ea5e9', boxShadow: '0 20px 25px -5px rgba(14, 165, 233, 0.1)', textAlign: 'left', position: 'relative' }}>
                <div style={{ position: 'absolute', top: '-12px', right: '32px', background: '#0ea5e9', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  The Eagentix Way
                </div>
                <div style={{ color: '#0ea5e9', fontWeight: 800, fontSize: '1.2rem', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#e0f2fe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</div>
                  Specialized Finance Training
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: '#0f172a', fontSize: '1rem', lineHeight: 1.7, display: 'flex', flexDirection: 'column', gap: '16px', fontWeight: 500 }}>
                  <li style={{ paddingLeft: '24px', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#0ea5e9' }}>✓</span>
                    <strong>Create Agents Day 1:</strong> Build custom Copilot Agents & SharePoint Agents.
                  </li>
                  <li style={{ paddingLeft: '24px', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#0ea5e9' }}>✓</span>
                    <strong>High Impact:</strong> Automate variance analysis, month-end close, & cash flow.
                  </li>
                  <li style={{ paddingLeft: '24px', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#0ea5e9' }}>✓</span>
                    <strong>Real Context:</strong> Trains exclusively on real-world GLs, P&Ls, and AR Aging reports.
                  </li>
                  <li style={{ paddingLeft: '24px', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#0ea5e9' }}>✓</span>
                    <strong>Immediate ROI:</strong> Build production-ready agents that eliminate hours of reconciliation.
                  </li>
                </ul>
              </div>
            </div>
            
            <ROICalculator />

            <div style={{ marginTop: '40px' }}>
              <a
                href="#lead-form"
                onClick={() => trackEvent('cta_click', { button: 'Post-ROI Book Training' })}
                style={{ background: 'linear-gradient(135deg, #0071e3, #2563eb)', color: '#ffffff', padding: '18px 40px', borderRadius: '12px', fontWeight: 900, textDecoration: 'none', fontSize: '1.15rem', boxShadow: '0 15px 35px -5px rgba(37, 99, 235, 0.4)', display: 'inline-block' }}
              >
                Book Your 1-Day Training →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* The Day, Hour by Hour */}
      <section style={sectionWhite}>
        <div style={container}>
          <div style={{ textAlign: 'center', marginBottom: '48px', maxWidth: '800px', margin: '0 auto 48px' }}>
            <span style={{ color: '#0071e3', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              The 1-Day Agenda
            </span>
            <h2 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', margin: '16px 0 16px' }}>
              One Day. Your Office. Hour by Hour.
            </h2>
            <p style={{ color: '#475569', fontSize: '1.1rem', lineHeight: 1.7 }}>
              A concrete product, not a vague workshop. We calibrate pace and examples to your team on the day.
            </p>
          </div>
          <div style={{ maxWidth: '760px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[
              { t: '9:00', h: 'Copilot for Finance Foundations', d: 'How Copilot works with financial data, prompting framework, controls and verification habits.' },
              { t: '10:30', h: 'Excel & Financial Analysis', d: 'GLs, P&Ls, variance analysis, formulas and summaries — on your own files.' },
              { t: '12:00', h: 'Finance Reporting', d: 'Management commentary, month-end outputs, reconciliation support.' },
              { t: '1:30', h: 'Advanced Finance Workflows', d: 'FP&A, budget-vs-actual, recurring analysis your team repeats every close.' },
              { t: '3:00', h: 'PowerPoint & Management Communication', d: 'Turn the afternoon’s analysis into CFO- and board-ready decks.' },
              { t: '4:00', h: 'Beyond Prompting: Build a Finance Agent', d: 'Create a practical Copilot / SharePoint Agent around one of your finance workflows.' },
              { t: '5:00', h: 'Take It Back to Work', d: 'Reusable prompts, workflows and reference materials your team keeps.' },
            ].map((s) => (
              <div key={s.t} style={{ display: 'flex', gap: '20px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px 24px', alignItems: 'baseline' }}>
                <div style={{ color: '#0071e3', fontWeight: 900, fontSize: '1.05rem', minWidth: '56px' }}>{s.t}</div>
                <div>
                  <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '1.1rem', marginBottom: '4px' }}>{s.h}</div>
                  <div style={{ color: '#475569', fontSize: '0.98rem', lineHeight: 1.6 }}>{s.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* Alumni Example */}
      <section style={{ padding: '80px 0', background: '#f8fafc' }} id="example">
        <div style={container}>
          <div style={{ textAlign: 'center', marginBottom: '48px', maxWidth: '800px', margin: '0 auto 48px' }}>
            <span style={{ color: '#0071e3', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              Alumni Showcase
            </span>
            <h2 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', margin: '16px 0 16px' }}>
              See What You'll Build
            </h2>
            <p style={{ color: '#475569', fontSize: '1.1rem', lineHeight: 1.7 }}>
              Curious about the quality of output? This is a sample Board Presentation generated using sample finance data. Using <strong>Copilot for Excel</strong>, raw GL data is quickly reconciled into clean tables, and then formatted with <strong>Copilot for PowerPoint</strong> into a polished deck—all in under 15 minutes.
            </p>
          </div>
          
          <div style={{ position: 'relative', maxWidth: '900px', margin: '0 auto' }}>
            <DeckCarousel />
          </div>
        </div>
      </section>

      {/* What Your Team Leaves With */}
      <section style={sectionLight}>
        <div style={container}>
          <div style={{ textAlign: 'center', marginBottom: '48px', maxWidth: '800px', margin: '0 auto 48px' }}>
            <span style={{ color: '#0071e3', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              Tangible Deliverables
            </span>
            <h2 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', margin: '16px 0 16px' }}>
              What Your Team Leaves With
            </h2>
            <p style={{ color: '#475569', fontSize: '1.1rem', lineHeight: 1.7 }}>
              Not just knowledge. Your team walks out with working assets they use the next morning.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', maxWidth: '1000px', margin: '0 auto' }}>
            {[
              { t: 'Reusable Finance Prompt Library', d: 'Tested prompts for variance analysis, commentary, reconciliations and reporting — ready to copy and use.' },
              { t: 'Working Copilot Workflows', d: 'The workflows your team built during the day, on your own data, inside your own tenant.' },
              { t: 'Prompting Framework for Finance', d: 'A repeatable method for getting reliable outputs from financial data — not one-off tricks.' },
              { t: 'Finance-Specific Templates & Examples', d: 'Month-end, FP&A and board-reporting examples your team can adapt to their own files.' },
              { t: "Do's and Don'ts for Financial Data", d: 'Where Copilot is reliable, where it is not, and how to verify outputs before they reach management.' },
              { t: 'Reference Playbook', d: 'Take-home materials so the training keeps paying off after the day ends.' },
            ].map((item) => (
              <div key={item.t} style={{ ...card, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <CheckCircle size={22} color="#10b981" weight="fill" />
                  <div style={{ fontWeight: 800, fontSize: '1.05rem', color: '#0f172a' }}>{item.t}</div>
                </div>
                <div style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.6 }}>{item.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Conducts the Training */}
      <section style={sectionWhite}>
        <div style={container}>
          <div style={{ textAlign: 'center', marginBottom: '40px', maxWidth: '800px', margin: '0 auto 40px' }}>
            <span style={{ color: '#0071e3', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              Your Trainer
            </span>
            <h2 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', margin: '16px 0 16px' }}>
              Not Taught by an AI Generalist
            </h2>
          </div>
          <div className="split-flex" style={{ display: 'flex', gap: '40px', alignItems: 'center', maxWidth: '900px', margin: '0 auto', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '24px', padding: '40px' }}>
            <img
              src="/images/abhijit-ghosh.jpg"
              alt="Abhijit Ghosh, Founder of Eagentix and lead trainer"
              style={{ flexShrink: 0, width: '160px', height: '160px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #0071e3', boxShadow: '0 10px 30px rgba(15, 23, 42, 0.15)' }}
            />
            <div>
              <div style={{ fontWeight: 900, fontSize: '1.4rem', color: '#0f172a' }}>Abhijit Ghosh</div>
              <div style={{ color: '#0071e3', fontWeight: 700, fontSize: '1rem', marginBottom: '16px' }}>Founder, Eagentix · Data & Generative AI Practitioner · 25 Years · Singapore</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px 0', color: '#334155', fontSize: '1rem', lineHeight: 1.7, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <li style={{ paddingLeft: '24px', position: 'relative' }}><span style={{ position: 'absolute', left: 0, color: '#0ea5e9', fontWeight: 800 }}>✓</span>25 years delivering large-scale data, analytics, AI and Generative AI platforms across <strong>Accenture, IBM, Deloitte, Cloudera, Tibco and Wipro</strong></li>
                <li style={{ paddingLeft: '24px', position: 'relative' }}><span style={{ position: 'absolute', left: 0, color: '#0ea5e9', fontWeight: 800 }}>✓</span>Deep experience in <strong>banking, insurance and regulated environments</strong> — moving AI initiatives from proof-of-concept to production with security and compliance in scope</li>
                <li style={{ paddingLeft: '24px', position: 'relative' }}><span style={{ position: 'absolute', left: 0, color: '#0ea5e9', fontWeight: 800 }}>✓</span>Current focus on Generative AI and agentic workflows — builds enterprise finance agents (reconciliation, KYC, accounting) at Eagentix</li>
              </ul>
              <a
                href="https://www.linkedin.com/in/abhijitghoshsingapore/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('trainer_linkedin_click', {})}
                style={{ color: '#0071e3', fontWeight: 800, textDecoration: 'none', fontSize: '1rem', border: '1px solid #0071e3', padding: '10px 20px', borderRadius: '10px', display: 'inline-block' }}
              >
                View LinkedIn Profile →
              </a>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <a
              href="#lead-form"
              onClick={() => trackEvent('cta_click', { button: 'Post-Trainer Book Training' })}
              style={{ background: 'linear-gradient(135deg, #0071e3, #2563eb)', color: '#ffffff', padding: '18px 40px', borderRadius: '12px', fontWeight: 900, textDecoration: 'none', fontSize: '1.15rem', boxShadow: '0 15px 35px -5px rgba(37, 99, 235, 0.4)', display: 'inline-block' }}
            >
              Book Your 1-Day Training →
            </a>
          </div>
        </div>
      </section>

      {/* What the day produces */}
      <section style={{ background: '#f8fafc', padding: '80px 0', borderTop: '1px solid #e2e8f0' }}>
        <div style={{ ...container, maxWidth: '850px' }}>
          <div style={{ 
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', 
            borderRadius: '24px', 
            padding: '48px', 
            border: '2px solid #38bdf8', 
            boxShadow: '0 25px 50px -12px rgba(56, 189, 248, 0.25)', 
            textAlign: 'center' 
          }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(56, 189, 248, 0.15)', border: '1px solid #38bdf8', marginBottom: '20px' }}>
              <ShieldCheck size={36} color="#38bdf8" weight="duotone" />
            </div>
            <span style={{ color: '#38bdf8', fontWeight: 800, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', marginBottom: '12px' }}>
              What The Day Produces
            </span>
            <h3 style={{ fontSize: '2.2rem', fontWeight: 900, color: 'white', letterSpacing: '-0.02em', marginBottom: '16px' }}>
              Your team builds a working finance use case on day one.
            </h3>
            <p style={{ color: '#cbd5e1', fontSize: '1.1rem', lineHeight: 1.7, maxWidth: '700px', margin: '0 auto 28px' }}>
              By the end of the workshop your team will have applied Copilot to real finance workflows and built at least one working finance use case during the session — on your own data, demonstrated to the room. We agree the specific use case with you beforehand so the outcome is realistic for your team.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', flexWrap: 'wrap', color: '#94a3b8', fontSize: '0.9rem', fontWeight: 600 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircle size={20} color="#34d399" weight="fill" /> Use case agreed before the day
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Lock size={20} color="#38bdf8" weight="fill" /> Enterprise NDA & Data Confidentiality
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For vs NOT For (Negative Filter Section §3.9) */}
      <section style={{ ...sectionLight, padding: '80px 0' }}>
        <div style={container}>
          <div style={{ textAlign: 'center', marginBottom: '48px', maxWidth: '800px', margin: '0 auto' }}>
            <span style={{ color: '#0ea5e9', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', marginBottom: '12px' }}>
              Qualification Filter
            </span>
            <h2 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em' }}>
              Is This Onsite Training Right for Your Team?
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '32px', maxWidth: '950px', margin: '0 auto' }}>
            {/* IS FOR */}
            <div style={{ background: 'white', borderRadius: '24px', padding: '36px', border: '2px solid #10b981', boxShadow: '0 10px 30px rgba(16, 185, 129, 0.08)' }}>
              <div style={{ color: '#10b981', fontWeight: 800, fontSize: '1.3rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</div>
                Who This IS For
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '16px', color: '#334155', fontSize: '1rem', lineHeight: 1.6 }}>
                <li style={{ paddingLeft: '24px', position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#10b981', fontWeight: 800 }}>✓</span>
                  <strong>CFOs, Controllers & FP&A Teams</strong> struggling with manual Month-End Close bottlenecks.
                </li>
                <li style={{ paddingLeft: '24px', position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#10b981', fontWeight: 800 }}>✓</span>
                  Organizations with active <strong>Microsoft 365 Copilot</strong> or <strong>SharePoint Agent</strong> licenses.
                </li>
                <li style={{ paddingLeft: '24px', position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#10b981', fontWeight: 800 }}>✓</span>
                  Teams that want hands-on training on <strong>their actual P&L, Trial Balance, & GL datasets</strong>.
                </li>
              </ul>
            </div>

            {/* IS NOT FOR */}
            <div style={{ background: 'white', borderRadius: '24px', padding: '36px', border: '1px solid #cbd5e1', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
              <div style={{ color: '#ef4444', fontWeight: 800, fontSize: '1.3rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</div>
                Who This Is NOT For
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '16px', color: '#64748b', fontSize: '1rem', lineHeight: 1.6 }}>
                <li style={{ paddingLeft: '24px', position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#ef4444', fontWeight: 800 }}>✕</span>
                  Teams looking for generic IT training on drafting emails or summarizing meeting transcripts.
                </li>
                <li style={{ paddingLeft: '24px', position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#ef4444', fontWeight: 800 }}>✕</span>
                  Individual freelancers or teams without Microsoft enterprise Copilot environments.
                </li>
                <li style={{ paddingLeft: '24px', position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#ef4444', fontWeight: 800 }}>✕</span>
                  Companies expecting theoretical lectures without active hands-on workflow building.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section (Objection Handling) */}
      <section style={{ ...sectionWhite, padding: '80px 0' }} id="faq">
        <div style={container}>
          <div style={{ textAlign: 'center', marginBottom: '48px', maxWidth: '800px', margin: '0 auto 48px' }}>
            <span style={{ color: '#0071e3', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              Frequently Asked Questions
            </span>
            <h2 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', margin: '16px 0 16px' }}>
              Everything You Need to Know
            </h2>
            <p style={{ color: '#475569', fontSize: '1.1rem', lineHeight: 1.7 }}>
              Questions about security, curriculum or how the day runs.
            </p>
          </div>
          <FinanceFAQ />
        </div>
      </section>

      {/* Lead Capture */}
      <section style={sectionDark} id="lead-form">
        <div style={{ ...container, maxWidth: '600px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <span style={{ color: '#38bdf8', fontWeight: 800, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', marginBottom: '12px' }}>
              By Application
            </span>
            <h2 style={{ fontSize: '2.5rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', marginBottom: '16px' }}>
              You've Already Invested in Copilot.
            </h2>
            <p style={{ color: '#e2e8f0', fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.5, marginBottom: '16px' }}>
              Now invest one day in teaching Finance how to use it.
            </p>
            <p style={{ color: '#cbd5e1', fontSize: '1.1rem', lineHeight: 1.6 }}>
              Every session is private and built around your own finance data, so we scope each one before we book it.
            </p>
            <p style={{ color: '#94a3b8', fontSize: '0.95rem', lineHeight: 1.6, marginTop: '12px' }}>
              One flat day rate for your whole team — no per-seat pricing. Weigh it against the unused license spend in the calculator above.
            </p>
          </div>

          <LeadForm
            page="/copilot-finance-training"
            showTeamSize
            title="Request Onsite Training"
            subtitle="We come to your office. Private session, actual finance data."
            buttonLabel="Request Onsite Training"
            messageLabel="What's your biggest finance bottleneck? (optional)"
            messagePlaceholder="e.g. Month-end close takes 8 days, board decks are entirely manual..."
            successText="Thanks — we'll respond within 24 hours to scope a private onsite session for your finance team."
          />

          {/* P.S. Section (§3.12) */}
          <div style={{ marginTop: '40px', padding: '24px', borderRadius: '16px', background: 'rgba(30, 41, 59, 0.8)', border: '1px dashed #38bdf8', textAlign: 'center' }}>
            <p style={{ color: '#94a3b8', fontSize: '0.95rem', lineHeight: 1.6, margin: 0, fontStyle: 'italic' }}>
              <strong style={{ color: '#38bdf8', fontStyle: 'normal' }}>P.S.</strong> All hands-on exercises run privately inside your organisation's Microsoft 365 environment, under your existing tenant permissions and an enterprise NDA.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
